import { useState } from 'react';
import { Button } from '@appwin/ui';
import { api, ApiError, type ReleaseView, type SubmitReleaseInput } from './api.js';

/**
 * Release form for the link-only catalogue.
 *
 * Appwin does not host installers: the developer supplies the publisher's own
 * download URL plus its SHA-256, and the client verifies the hash at install
 * time. The hash field is therefore mandatory and cannot be skipped.
 */
export function AddReleaseDialog({
  appId,
  onClose,
  onSubmitted,
}: {
  appId: string;
  onClose: () => void;
  onSubmitted: (release: ReleaseView) => void;
}) {
  const [version, setVersion] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');
  const [sha256, setSha256] = useState('');
  const [sizeMb, setSizeMb] = useState('');
  const [kind, setKind] = useState<SubmitReleaseInput['kind']>('exe');
  const [arch, setArch] = useState<SubmitReleaseInput['arch'][number]>('x64');
  const [scope, setScope] = useState<SubmitReleaseInput['scope']>('per_user');
  const [silent, setSilent] = useState('/S');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsSilent = kind === 'exe' || kind === 'msi';

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const bytes = sizeMb.trim() ? Math.round(Number(sizeMb.replace(',', '.')) * 1024 * 1024) : undefined;
      const input: SubmitReleaseInput = {
        version: version.trim(),
        platform: 'windows',
        arch: [arch],
        kind,
        scope,
        downloadUrl: downloadUrl.trim(),
        sha256: sha256.trim().toLowerCase(),
        ...(bytes && Number.isFinite(bytes) && bytes > 0 ? { sizeBytes: bytes } : {}),
        ...(needsSilent
          ? {
              silentArgs: silent.split(/\s+/).filter(Boolean),
              upgradeArgs: silent.split(/\s+/).filter(Boolean),
            }
          : {}),
      };
      const { release } = await api.submitRelease(appId, input);
      onSubmitted(release);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Не удалось добавить версию');
    } finally {
      setBusy(false);
    }
  };

  const valid =
    version.trim().length > 0 &&
    /^https:\/\/.+/i.test(downloadUrl.trim()) &&
    /^[a-f0-9]{64}$/i.test(sha256.trim()) &&
    (!needsSilent || silent.trim().length > 0);

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-surface p-6">
        <h2 className="text-lg font-semibold">Добавить версию</h2>
        <p className="mt-1 text-xs text-muted">{appId}</p>
        <p className="mt-3 rounded-lg border border-border bg-bg p-3 text-xs text-muted">
          Appwin не хранит установщики. Укажите прямую ссылку на файл с сайта издателя и его
          контрольную сумму — клиент проверит файл перед установкой.
        </p>

        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Версия">
              <input
                value={version}
                onChange={(event) => setVersion(event.target.value)}
                placeholder="1.0.0"
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              />
            </Field>
            <Field label="Тип пакета">
              <select
                value={kind}
                onChange={(event) => setKind(event.target.value as SubmitReleaseInput['kind'])}
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              >
                <option value="exe">EXE</option>
                <option value="msi">MSI</option>
                <option value="msix">MSIX</option>
                <option value="appx">APPX</option>
                <option value="zip">ZIP (портативная)</option>
              </select>
            </Field>
          </div>

          <Field label="Ссылка на установщик" hint="Только https. Прямая ссылка на файл, не на страницу загрузки.">
            <input
              value={downloadUrl}
              onChange={(event) => setDownloadUrl(event.target.value)}
              placeholder="https://vendor.example/app-1.0.0.exe"
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
            />
          </Field>

          <Field
            label="SHA-256 файла"
            hint="64 символа. Без него нельзя проверить загрузку, поэтому без хеша версия не принимается."
          >
            <input
              value={sha256}
              onChange={(event) => setSha256(event.target.value)}
              placeholder="a1b2c3…"
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 font-mono text-sm"
            />
            {sha256.trim().length > 0 && !/^[a-f0-9]{64}$/i.test(sha256.trim()) ? (
              <span className="mt-1 block text-xs text-danger">
                Нужно ровно 64 шестнадцатеричных символа
              </span>
            ) : null}
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Размер, МБ" hint="необязательно">
              <input
                value={sizeMb}
                onChange={(event) => setSizeMb(event.target.value)}
                placeholder="12.5"
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              />
            </Field>
            <Field label="Архитектура">
              <select
                value={arch}
                onChange={(event) => setArch(event.target.value as SubmitReleaseInput['arch'][number])}
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              >
                <option value="x64">x64</option>
                <option value="x86">x86</option>
                <option value="arm64">ARM64</option>
                <option value="universal">универсальная</option>
              </select>
            </Field>
            <Field label="Установка">
              <select
                value={scope}
                onChange={(event) => setScope(event.target.value as SubmitReleaseInput['scope'])}
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              >
                <option value="per_user">для пользователя</option>
                <option value="machine">для всех</option>
                <option value="portable">портативная</option>
              </select>
            </Field>
          </div>

          {needsSilent ? (
            <Field
              label="Параметры тихой установки"
              hint="Без них приложение нельзя обновлять автоматически."
            >
              <input
                value={silent}
                onChange={(event) => setSilent(event.target.value)}
                placeholder="/S"
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 font-mono text-sm"
              />
            </Field>
          ) : null}
        </div>

        {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={busy || !valid}>
            {busy ? 'Отправка…' : 'Отправить на проверку'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null}
    </label>
  );
}
